import { section, p, list, steps, tip, warn, table, faq } from './blocks';

export default {
  slug: 'warenwirtschaft-einkauf',
  title: 'Warenwirtschaft & Einkauf',
  shortTitle: 'Warenwirtschaft',
  icon: 'Package',
  updated: 'Juni 2026',
  datePublished: '2026-06-24',
  dateModified: '2026-06-24',
  image: 'https://images.unsplash.com/photo-1567335632614-abebe179111e?w=1200&h=900&fit=crop',
  teaser: 'Bestände, Rezepturen, Lieferanten und Inventur digital im Griff: Wie Warenwirtschaft Verderb reduziert, Margen sichert und den Einkauf vereinfacht.',
  mainArticle: {
    title: 'Digitale Warenwirtschaft und Einkauf: Der große Leitfaden',
    takeaways: [
      'Jeder Prozentpunkt weniger Wareneinsatz sind bei 600.000 € Umsatz 6.000 € mehr Ergebnis.',
      'Rezepturen verbinden Kasse und Bestand – der Soll-Ist-Vergleich macht Schwund und Verderb sichtbar.',
      'Mit den Top-50-Artikeln starten, Wareneingänge konsequent buchen, monatlich Inventur.',
      'Warenwirtschaft funktioniert nur mit Disziplin und einer klar verantwortlichen Person.',
    ],
    intro: 'Zwischen Wareneingang und Teller verschwinden in vielen Betrieben drei bis sechs Prozent des Umsatzes – durch Verderb, Schwund, Überportionierung und Fehlbestellungen. Digitale Warenwirtschaft macht diese Verluste sichtbar und steuerbar. Dieser Leitfaden zeigt, was ein System leistet, was es an Pflege verlangt und wie du es Schritt für Schritt einführst.',
    sections: [
      section(
        'Warum Warenwirtschaft in der Gastronomie oft vernachlässigt wird',
        p('Kasse, Reservierung und Personalplanung haben einen unmittelbar spürbaren Nutzen im Alltag. Warenwirtschaft dagegen erfordert Pflegeaufwand – Wareneingänge buchen, Rezepturen anlegen, Inventur zählen – und der Ertrag zeigt sich erst in der Auswertung. Deshalb bleibt sie häufig bei einer Einkaufsliste am Kühlhaus.'),
        p('Dabei ist der Hebel enorm: Bei 600.000 € Jahresumsatz und 30 % Wareneinsatz gehen 180.000 € durch den Wareneinkauf. Jeder Prozentpunkt weniger Wareneinsatz sind **6.000 € mehr Ergebnis** – und Betriebe, die Warenwirtschaft einführen, senken den Wareneinsatz typischerweise um zwei bis vier Prozentpunkte.')
      ),
      section(
        'Was ein Warenwirtschaftssystem leistet',
        list([
          '**Artikelstamm:** Alle Rohwaren mit Einheit, Lieferant, Preis, Lagerort, Mindestbestand',
          '**Lieferanten und Preislisten:** Aktuelle Preise je Lieferant, Preisänderungen im Verlauf',
          '**Bestellungen:** Bestellvorschläge aus Verbrauch und Mindestbestand, Versand an Lieferanten',
          '**Wareneingang:** Lieferung gegen Bestellung prüfen, Mengen und Preise buchen, Reklamationen',
          '**Bestandsführung:** Aktueller Bestand je Artikel und Lagerort',
          '**Rezepturen:** Zutaten und Mengen je Gericht, Wareneinsatz je Portion, Allergene',
          '**Kassenanbindung:** Verkäufe buchen Bestände über Rezepturen ab',
          '**Inventur:** Digitale Zähllisten, Soll-Ist-Vergleich, Bewertung',
          '**Auswertungen:** Wareneinsatz Soll/Ist, Verderb, Preisentwicklung, Lieferantenvergleich',
        ])
      ),
      section(
        'Verbindung zur Kasse: Vom Verkauf zum Bestandsabbau',
        p('Der entscheidende Schritt ist die Verknüpfung von Kasse und Warenwirtschaft über Rezepturen. Wird ein Gericht verkauft, bucht das System die hinterlegten Zutaten automatisch vom Bestand ab. Daraus entsteht der **Soll-Bestand** – was theoretisch noch da sein müsste. Der Vergleich mit der Inventur (Ist-Bestand) zeigt Differenzen, die durch Verkäufe nicht erklärt sind.'),
        table(
          ['Artikel', 'Anfangsbestand', 'Einkauf', 'Verbrauch laut Rezepturen', 'Soll-Bestand', 'Inventur (Ist)', 'Differenz'],
          [
            ['Rinderfilet (kg)', '12,0', '40,0', '44,5', '7,5', '5,8', '−1,7 kg (ca. 60 €)'],
            ['Gin (Flaschen)', '8', '24', '26,2', '5,8', '4', '−1,8 Fl. (ca. 45 €)'],
            ['Olivenöl (Liter)', '15', '20', '18,0', '17', '16,5', '−0,5 l'],
          ]
        ),
        p('Solche Differenzen sind der Ausgangspunkt für Gespräche über Portionierung, Schwund, Verderb oder fehlerhafte Rezepturen. Ohne Warenwirtschaft bleiben sie unsichtbar – und summieren sich.')
      ),
      section(
        'Einkauf digitalisieren',
        p('Der Einkauf per Telefon, Fax oder WhatsApp an fünf verschiedene Lieferanten kostet Zeit und produziert Fehler. Digitaler Einkauf bedeutet:'),
        list([
          '**Bestellvorschläge** aus Mindestbestand, Verbrauch und Reservierungslage',
          '**Eine Bestelloberfläche** für alle Lieferanten – Versand per E-Mail, Bestellportal oder Schnittstelle',
          '**Preisvergleich** zwischen Lieferanten für denselben Artikel',
          '**Liefertermine und Bestellschluss** je Lieferant hinterlegt',
          '**Wareneingang gegen Bestellung**: Fehlmengen und Preisabweichungen sofort sichtbar',
          '**Rechnungsabgleich**: Lieferschein, Rechnung und Bestellung stimmen überein – oder nicht',
        ]),
        tip('Allein der Rechnungsabgleich rechnet sich: Preisabweichungen zwischen Angebot und Rechnung, doppelt berechnete Positionen und nicht gutgeschriebene Reklamationen summieren sich in vielen Betrieben auf mehrere hundert Euro im Monat.')
      ),
      section(
        'Rezepturen und Kalkulation',
        p('Rezepturen sind das Herz der Warenwirtschaft: Sie verbinden Einkauf und Verkauf. Jede Rezeptur enthält Zutaten, Mengen (inklusive Verlust beim Putzen und Garen), Zubereitungsschritte und den daraus berechneten Wareneinsatz. Ändert ein Lieferant den Preis, aktualisieren sich alle betroffenen Rezepturen – und du siehst sofort, welche Gerichte unter Druck geraten. Wie du Rezepturen anlegst und Verkaufspreise kalkulierst, erklärt der Vertiefungsartikel.')
      ),
      section(
        'Inventur',
        p('Ohne Inventur ist die Wareneinsatzquote eine Einkaufsquote. Digitale Inventur mit Zähllisten nach Lagerort, Scanner oder App verkürzt die Zählung von Stunden auf Minuten – und macht monatliche oder sogar wöchentliche Inventuren für Getränke realistisch. Details im Vertiefungsartikel zur digitalen Inventur.')
      ),
      section(
        'Kosten und Aufwand ehrlich betrachtet',
        table(
          ['Kostenart', 'Typische Bandbreite', 'Hinweis'],
          [
            ['Software', 'ca. 50 – 250 € / Monat und Standort', 'Oft als Modul der Kasse oder eigenständig'],
            ['Einrichtung', 'ca. 500 – 3.000 €', 'Artikelstamm, Lieferanten, Rezepturen – oft der größte Aufwand'],
            ['Hardware', 'ca. 0 – 800 €', 'Tablet/Scanner für Inventur, Waage'],
            ['Laufender Pflegeaufwand', 'ca. 2 – 5 Std. / Woche', 'Wareneingang buchen, Rezepturen pflegen, Inventur'],
          ]
        ),
        warn('Warenwirtschaft funktioniert nur mit Disziplin. Wer Wareneingänge nicht bucht oder Rezepturen nicht pflegt, bekommt falsche Zahlen – und verliert das Vertrauen ins System. Plane die Pflegezeit fest ein und benenne eine verantwortliche Person.')
      ),
      section(
        'Worauf es bei der Systemauswahl ankommt',
        steps([
          '**Kassenanbindung:** Werden Verkäufe automatisch über Rezepturen abgebucht?',
          '**Bedienbarkeit in der Küche:** Kann der Küchenchef Wareneingänge und Rezepturen ohne Büro-Umweg pflegen?',
          '**Lieferantenanbindung:** Import von Preislisten, digitale Bestellung, Rechnungsabgleich?',
          '**Inventur-Funktion:** Zähllisten nach Lagerort, App oder Scanner?',
          '**Rezepturkalkulation:** Verluste, Zwischenprodukte (z. B. Fonds), Allergene?',
          '**Auswertungen:** Soll-Ist-Wareneinsatz, Verderb, Preisentwicklung?',
          '**Mehrfilialfähigkeit** und zentraler Einkauf, falls relevant.',
        ])
      ),
      section(
        'Einführung in Phasen',
        p('Der häufigste Fehler ist, alles auf einmal zu wollen. Bewährt hat sich ein Aufbau in Stufen:'),
        steps([
          '**Top-50-Artikel:** Die 50 Rohwaren mit dem höchsten Einkaufsvolumen anlegen – sie machen meist 80 % des Wareneinsatzes aus.',
          '**Wareneingang buchen:** Für diese Artikel jede Lieferung erfassen. Nach vier Wochen ist die Routine da.',
          '**Erste Inventur:** Bestände der Top-50 zählen, Ist-Wareneinsatz berechnen.',
          '**Rezepturen für die Top-20-Gerichte:** Soll-Wareneinsatz und erste Soll-Ist-Vergleiche.',
          '**Ausweiten:** Weitere Artikel, Rezepturen, Getränke, Bestellautomatik.',
          '**Einkauf digitalisieren:** Bestellvorschläge und Lieferantenanbindung.',
        ])
      ),
      section(
        'Woran Warenwirtschaft in der Praxis scheitert',
        list([
          '**Zu detailliert starten:** 800 Artikel und 200 Rezepturen am ersten Tag – das System stirbt an der Pflege',
          '**Wareneingang nicht buchen:** Ohne Eingänge sind Bestände und Wareneinsatz wertlos',
          '**Rezepturen ohne Verluste:** Putz-, Gar- und Schnittverluste von 10 – 40 % fehlen in der Kalkulation',
          '**Keine Inventur:** Soll-Bestände ohne Ist-Vergleich decken keine Differenzen auf',
          '**Küche nicht eingebunden:** Wenn der Küchenchef das System als Kontrolle statt als Werkzeug versteht, scheitert es',
          '**Preise nicht aktualisieren:** Rezepturkosten von vor zwei Jahren führen zu falschen Verkaufspreisen',
        ])
      ),
      section(
        'Was uns Küchenchefs häufig fragen',
        faq([
          { q: 'Ab welcher Betriebsgröße lohnt sich Warenwirtschaft?', a: 'Ab etwa 300.000 € Jahresumsatz lohnt sich ein System fast immer, weil ein bis zwei Prozentpunkte Wareneinsatz die Kosten deutlich übersteigen. Kleinere Betriebe profitieren bereits von einer reduzierten Variante: Top-Artikel, monatliche Inventur, Rezepturkalkulation für die Karte.' },
          { q: 'Muss die Küche jeden Wareneingang einzeln buchen?', a: 'Bei digitaler Bestellung wird der Wareneingang gegen die Bestellung bestätigt – nur Abweichungen müssen erfasst werden. Manche Lieferanten liefern digitale Lieferscheine, die automatisch eingelesen werden. Der Aufwand pro Lieferung liegt dann bei ein bis drei Minuten.' },
          { q: 'Was ist mit Zwischenprodukten wie Fonds, Saucen oder Teigen?', a: 'Gute Systeme kennen mehrstufige Rezepturen: Ein Fond ist selbst eine Rezeptur aus Rohwaren und wird in anderen Gerichten als Zutat verwendet. So bleiben Kalkulation und Abbuchung korrekt.' },
          { q: 'Wie genau muss die Inventur sein?', a: 'Genauer, als du denkst – aber nicht perfekt. Wiege Anbruch statt zu schätzen, zähle Getränke pro Flasche und nutze feste Zählreihenfolgen. Eine Inventur mit 2 – 3 % Ungenauigkeit ist besser als keine.' },
        ])
      ),
    ],
  },
  subArticles: [
    {
      slug: 'inventur-digital',
      title: 'Inventur digital: Schneller, genauer und häufiger zählen',
      shortTitle: 'Digitale Inventur',
      teaser: 'Wie digitale Zähllisten, Scanner und Apps die Inventur von einem Halbtagesprojekt zu einer Routine machen – und was du aus den Ergebnissen ableitest.',
      intro: 'Inventur gilt als lästig, dauert lange und wird deshalb selten gemacht. Dabei ist sie die einzige Möglichkeit, den tatsächlichen Wareneinsatz zu kennen. Digital wird sie so schnell, dass sie monatlich – für Getränke sogar wöchentlich – machbar ist.',
      sections: [
        section(
          'Warum häufiger zählen?',
          p('Eine Jahresinventur erfüllt die steuerliche Pflicht, hilft aber nicht beim Steuern. Wer monatlich zählt, kennt den Ist-Wareneinsatz jeden Monat und erkennt Differenzen, solange sie klein sind. Getränke – hochpreisig, gut zählbar, schwundanfällig – lohnen eine wöchentliche Zählung der wichtigsten Positionen.')
        ),
        section(
          'Der digitale Ablauf',
          steps([
            '**Zähllisten nach Lagerort:** Kühlhaus, Trockenlager, Bar, Tiefkühlung – in der Reihenfolge, in der die Ware im Regal steht',
            '**Zählen mit Tablet oder Smartphone:** Menge eingeben oder Barcode scannen; Anbruch wiegen oder in Zehnteln schätzen',
            '**Mehrere Personen parallel:** Jeder Lagerort ein Zähler, alles läuft in eine Inventur',
            '**Automatische Bewertung:** Bestand × aktueller Einkaufspreis',
            '**Soll-Ist-Vergleich:** Das System zeigt sofort, wo Ist und Soll auseinanderlaufen',
            '**Freigabe und Abschluss:** Inventur wird gebucht, neue Bestände sind Ausgangspunkt für den nächsten Monat',
          ]),
          p('Eine gut vorbereitete Monatsinventur dauert in einem mittleren Restaurant mit digitaler Unterstützung 60 – 90 Minuten statt eines halben Tages.')
        ),
        section(
          'Der richtige Rhythmus',
          table(
            ['Warengruppe', 'Rhythmus', 'Begründung'],
            [
              ['Spirituosen, Wein, hochpreisige Getränke', 'Wöchentlich (Top-Positionen)', 'Hoher Wert, gut zählbar, schwundanfällig'],
              ['Fleisch, Fisch, hochpreisige Frischware', 'Wöchentlich oder zweiwöchentlich', 'Hoher Wert, Verderbsrisiko'],
              ['Alle übrigen Lebensmittel', 'Monatlich', 'Grundlage für Monatswareneinsatz'],
              ['Non-Food, Verpackung, Reinigung', 'Quartalsweise', 'Geringer Wert, stabiler Verbrauch'],
              ['Gesamtinventur', 'Jährlich zum Bilanzstichtag', 'Steuerliche Pflicht'],
            ]
          )
        ),
        section(
          'Aus den Ergebnissen lernen',
          p('Die Inventur ist kein Selbstzweck. Die Differenzliste zwischen Soll und Ist ist die Grundlage für Gespräche und Maßnahmen:'),
          list([
            'Hohe Differenz bei einem Artikel: Rezeptur prüfen (Menge korrekt?), Portionierung wiegen, Verderb erfassen',
            'Hohe Differenz an der Bar: Ausschankmengen, Freigetränke, Bruch – Buchungsdisziplin klären',
            'Differenz systematisch positiv (mehr da als erwartet): Rezepturen zu großzügig oder Wareneingang doppelt gebucht',
            'Verderb erfassen: Abschreibungsliste in der Küche, monatlich auswerten',
          ]),
          tip('Führe eine „Bruch- und Verderbliste“ direkt neben dem Abfall: Was wird weggeworfen, wie viel, warum? Zusammen mit der Inventurdifferenz zeigt sie, ob das Problem Verderb, Portionierung oder Schwund ist.')
        ),
        section(
          'Häufige Fehlerquellen',
          list([
            'Unterschiedliche Einheiten zwischen Einkauf (Karton), Rezeptur (Gramm) und Inventur (Stück)',
            'Anbruch geschätzt statt gewogen – bei Spirituosen und Öl schnell mehrere Prozent',
            'Wareneingänge zwischen Zählung und Buchung nicht berücksichtigt',
            'Ware an mehreren Orten (Kühlhaus, Küche, Bar) ohne vollständige Zählung',
            'Inventur zu unterschiedlichen Zeitpunkten (vor/nach Service) – immer zur gleichen Zeit zählen',
          ])
        ),
      ],
    },
    {
      slug: 'rezepturen-kalkulation',
      title: 'Rezepturen und Kalkulation: Vom Wareneinsatz zum richtigen Verkaufspreis',
      shortTitle: 'Rezepturen & Kalkulation',
      teaser: 'Wie du Rezepturen mit Verlusten korrekt anlegst, den Wareneinsatz pro Portion berechnest und daraus Verkaufspreise ableitest, die Marge und Markt zusammenbringen.',
      intro: 'Viele Verkaufspreise entstehen aus Gefühl und Wettbewerbsvergleich. Das funktioniert, bis Einkaufspreise steigen und niemand merkt, dass ein Gericht Verlust macht. Rezepturkalkulation gibt dir den Boden unter den Füßen.',
      sections: [
        section(
          'Eine Rezeptur richtig anlegen',
          p('Eine Rezeptur listet alle Zutaten eines Gerichts mit Menge in der Einkaufseinheit, aktuellem Preis und Verlustfaktor. Die drei häufigsten Fehler: Verluste vergessen, Beilagen und Garnituren weglassen, Preise veralten lassen.'),
          table(
            ['Zutat', 'Einsatzmenge', 'Verlust', 'Einkaufsmenge', 'Preis / kg', 'Kosten'],
            [
              ['Lachsfilet', '180 g', '15 % (Haut, Parüren)', '212 g', '24,00 €', '5,08 €'],
              ['Kartoffeln', '200 g', '25 % (Schälen)', '267 g', '1,20 €', '0,32 €'],
              ['Spinat', '120 g', '30 % (Putzen)', '171 g', '4,50 €', '0,77 €'],
              ['Butter, Zitrone, Gewürze', 'pauschal', '–', '–', '–', '0,45 €'],
              ['**Wareneinsatz pro Portion**', '', '', '', '', '**6,62 €**'],
            ]
          ),
          p('Ohne Verlustfaktoren läge der kalkulierte Wareneinsatz bei rund 5,50 € – eine Abweichung von fast 20 Prozent, die sich direkt in der Marge niederschlägt.')
        ),
        section(
          'Zwischenprodukte und Pauschalen',
          p('Fonds, Saucen, Teige und Dressings werden einmal als eigene Rezeptur angelegt (Ausbeute z. B. 5 Liter Fond aus 3 kg Knochen und Gemüse) und dann als Zutat in Gerichten verwendet. Für Kleinstmengen wie Gewürze, Öl oder Garnitur hat sich eine Pauschale von 2 – 4 % des Wareneinsatzes bewährt – besser als eine Kalkulation mit 0,3 g Pfeffer.')
        ),
        section(
          'Vom Wareneinsatz zum Verkaufspreis',
          p('Der klassische Weg ist der Aufschlag auf den Wareneinsatz. Bei einer Ziel-Wareneinsatzquote von 30 % ergibt sich der Nettopreis als Wareneinsatz ÷ 0,30, darauf kommt die Mehrwertsteuer:'),
          table(
            ['Schritt', 'Rechnung', 'Ergebnis'],
            [
              ['Wareneinsatz pro Portion', '', '6,62 €'],
              ['Nettoverkaufspreis bei 30 % WE-Quote', '6,62 ÷ 0,30', '22,07 €'],
              ['Bruttopreis mit 19 % MwSt.', '22,07 × 1,19', '26,26 €'],
              ['Psychologischer Preis', 'Runden', '26,50 € oder 26,90 €'],
            ]
          ),
          p('Die Ziel-Quote ist eine Steuerungsgröße, kein Naturgesetz. Gerichte mit hohem Wareneinsatz (Fleisch, Fisch) dürfen eine höhere Quote haben, wenn der Deckungsbeitrag in Euro stimmt; einfache Gerichte (Pasta, Salate) tragen mit niedriger Quote den Durchschnitt.')
        ),
        section(
          'Marktpreis und Deckungsbeitrag zusammenbringen',
          p('Der kalkulierte Preis ist die Untergrenze – der Marktpreis die Realität. Liegt der kalkulierte Preis über dem, was Gäste zahlen, hast du drei Optionen: Rezeptur anpassen (günstigere Komponente, kleinere Portion, andere Beilage), Gericht positionieren (Signature Dish mit Story rechtfertigt höhere Preise) oder streichen. Liegt er darunter, verschenkst du Marge – erhöhe in Schritten von 0,50 bis 1 €.'),
          tip('Aktualisiere Einkaufspreise in den Rezepturen mindestens monatlich – automatisch über die Warenwirtschaft oder manuell für die Top-Artikel. Ein Anstieg des Fleischpreises um 15 % verändert bei Fleischgerichten den Wareneinsatz um mehrere Prozentpunkte.')
        ),
        section(
          'Rezepturen im Alltag nutzen',
          list([
            'Als Produktionsanweisung für die Küche – gleichbleibende Qualität und Portion',
            'Als Allergenliste – aus den Zutaten automatisch abgeleitet',
            'Als Basis für Soll-Wareneinsatz und Inventurabgleich',
            'Als Grundlage für Menü-Engineering und Kartenwechsel',
            'Als Kalkulationsvorlage für Events, Buffets und Catering-Angebote',
          ])
        ),
      ],
    },
    {
      slug: 'lieferanten-bestellautomatik',
      title: 'Lieferantenanbindung und Bestellautomatik: Einkauf ohne Zettelwirtschaft',
      shortTitle: 'Lieferanten & Bestellautomatik',
      teaser: 'Bestellvorschläge aus Verbrauch und Bestand, digitale Bestellung bei allen Lieferanten, automatischer Rechnungsabgleich: So läuft moderner Gastro-Einkauf.',
      intro: 'Der Einkauf ist in vielen Betrieben der letzte Prozess mit Zettel, Telefon und Bauchgefühl. Digitalisiert spart er Zeit, verhindert Fehlbestellungen und deckt Preisabweichungen auf, die sonst niemand bemerkt.',
      sections: [
        section(
          'Der Lieferantenstamm',
          p('Grundlage ist ein gepflegter Lieferantenstamm: Kontaktdaten, Bestellweg (E-Mail, Portal, Schnittstelle), Bestellschluss, Liefertage, Mindestbestellwert, Zahlungsbedingungen und die aktuelle Preisliste je Artikel. Viele Lieferanten stellen Preislisten digital bereit, die sich importieren lassen – so bleiben Rezepturkosten aktuell.')
        ),
        section(
          'Bestellvorschläge statt Bauchgefühl',
          p('Das System berechnet aus aktuellem Bestand, durchschnittlichem Verbrauch, Mindestbestand und Lieferrhythmus, was bestellt werden sollte. Reservierungslage und Events können den Bedarf erhöhen. Der Küchenchef prüft den Vorschlag, passt an und gibt frei – in fünf Minuten statt einer halben Stunde am Kühlhaus.'),
          list([
            'Mindestbestand je Artikel definieren (Sicherheitsreserve bis zur nächsten Lieferung)',
            'Verbrauch aus Kassenverkäufen über Rezepturen ableiten',
            'Bestellvorschlag nach Lieferant gruppiert',
            'Manuelle Anpassung bleibt möglich – das System schlägt vor, der Mensch entscheidet',
          ])
        ),
        section(
          'Bestellung und Wareneingang',
          steps([
            '**Bestellung senden:** Per E-Mail, Lieferantenportal oder Schnittstelle direkt aus dem System – mit Artikelnummern des Lieferanten',
            '**Bestellbestätigung** ablegen, Liefertermin im Blick',
            '**Wareneingang prüfen:** Lieferung gegen Bestellung – fehlt etwas, stimmt die Menge, ist die Qualität in Ordnung?',
            '**Abweichungen erfassen:** Fehlmengen, Bruch, Reklamationen direkt buchen',
            '**Bestand aktualisieren:** Automatisch mit Bestätigung des Wareneingangs',
          ]),
          tip('Bestätige den Wareneingang direkt beim Abladen mit dem Tablet – nicht abends vom Lieferschein. So werden Abweichungen sofort dokumentiert und gegenüber dem Lieferanten belegbar.')
        ),
        section(
          'Rechnungsabgleich',
          p('Der stille Gewinn der Digitalisierung: Rechnungen werden automatisch gegen Bestellung und Wareneingang geprüft. Typische Funde sind Preisabweichungen zwischen Angebot und Rechnung, doppelt berechnete Positionen, nicht berechnete Gutschriften für Reklamationen und Rechnungen für nicht gelieferte Ware. In vielen Betrieben summieren sich diese Fehler auf ein bis zwei Prozent des Einkaufsvolumens.')
        ),
        section(
          'Lieferanten vergleichen und verhandeln',
          list([
            'Preisentwicklung je Artikel und Lieferant über die Zeit',
            'Gleicher Artikel bei mehreren Lieferanten – wer ist günstiger, bei welcher Qualität?',
            'Einkaufsvolumen je Lieferant als Grundlage für Jahresgespräche',
            'Liefertreue und Reklamationsquote je Lieferant',
            'Weniger Lieferanten mit höherem Volumen bringen oft bessere Konditionen und weniger Aufwand',
          ]),
          warn('Der günstigste Preis ist nicht immer die beste Entscheidung. Liefertreue, Qualität, Flexibilität bei Nachbestellungen und Reklamationsverhalten sind im Gastro-Alltag oft mehr wert als zwei Prozent Preisvorteil.')
        ),
      ],
    },
  ],
};
